import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import { Box, Grid, TextField } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF, GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import RewardsAPI from 'api/faber/RewardsAPI'
import NormalText from 'common/Typography/NormalText'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const formikInitialValues = {
  catalogId: '',
  paymentIntent: ''
}

const NewUserPlanSchema = Yup.object().shape({
  paymentIntent: Yup.string()
    .matches(/^pi_/, 'Payment Intent must start with "pi_"')
    .required('Payment Intent is required')
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

interface Props {
  handleClose: () => void
  userId: string
  iccid: string
}

export const NewUserPlanForm = ({ handleClose, userId, iccid }: Props) => {
  const faberToken = OrbitSession.get('faberToken')
  const { data: catalogIds } = useQuery(['getCatalogs'], async () =>
    RewardsAPI.getCatalogs(faberToken)
  )

  const { mutate: createUserPlanByICCID } = useMutation(
    OneTimePackApi.createUserPlanByICCID,
    {
      onSuccess: () => {
        toast.success('User plan created successfully')
        handleClose()
      },
      onError: () => {
        toast.error('Error creating user plan')
      }
    }
  )

  interface CatalogRow {
    id: string
    name: string
    description: string
    tags: string[]
  }
  const [catalogRows, setCatalogRows] = React.useState<CatalogRow[]>([])
  const [catalogSelectionModel, setCatalogSelectionModel] =
    React.useState<string>('')

  const columns: GridColDef[] = [
    {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
      renderHeader: () => <></>, // Renders an empty header to prevent multi selection
      hideable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
    },
    {
      field: 'dataInGB',
      headerName: 'Data in GB',
      width: 120,
      renderCell: (params) => {
        const value = params.value
        if (Number(value) === -1) return 'Unlimited'
        if (value != null && value !== '') return value
        return '-'
      }
    },
    {
      field: 'validityInDays',
      headerName: 'Validity',
      width: 120,
      renderCell(params) {
        return `${params.value} day${params.value > 1 ? 's': ''}`
      }
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 120,
      valueGetter: (params) => params.row?.price?.currency,
      renderCell: (params) => params.row?.price?.currency || '-'
    },
    {
      field: 'priceValue',
      headerName: 'Value',
      width: 120,
      valueGetter: (params) => Number(params.row?.price?.value ?? 0),
      renderCell(params) {
        const price = params.row?.price || {}
        let amount = '-'
        if (price?.value != null) {
          const numeric = Number(price.value)
          amount = numeric.toLocaleString(undefined, {
            minimumFractionDigits: 2
          })
        }
        return `${amount}`.trim()
      }
    },
    {
      field: 'imsi-method',
      headerName: 'IMSI Method',
      width: 120,
      valueGetter: (params) => params.row?.provisionMethod?.method,
      renderCell: (params) => params.row?.provisionMethod?.method || '-'
    },
    {
      field: 'packageType',
      headerName: 'Package Type',
      width: 120
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 200
    }
  ]

  useEffect(() => {
    if (catalogIds) {
      setCatalogRows(catalogIds)
    }
  }, [catalogIds])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewUserPlanFormikProps) => {
    const allValues = {
      userId: userId,
      catalogId: catalogSelectionModel.split("-")[0],
      paymentIntent: values.paymentIntent,
      faberToken: faberToken,
      iccid: iccid
    }
    createUserPlanByICCID(allValues)
  }

  /**
   * Event handlers
   */

  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewUserPlanSchema}
    >
      {({ submitForm, handleChange, errors, touched, handleBlur }) => (
        <Form>
          <Grid container rowSpacing={2}>
            <Grid item xs={3}>
              <NormalText isBold>Catalog ID</NormalText>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                getRowId={(row) => `${row?.id}-${row?._index}`}
                checkboxSelection
                rows={catalogRows.map((r, i) => ({ ...r, _index: i }))} // add _index
                columns={columns}
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={
                  catalogSelectionModel ? [catalogSelectionModel] : []
                }
                onSelectionModelChange={(newSelection) => {
                  // Keep only the last selected row
                  const lastSelected = newSelection.slice(-1)[0]
                  setCatalogSelectionModel(lastSelected as string)
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Payment Intent</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'payment-intent'
                  }}
                  name='paymentIntent'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.paymentIntent && touched.paymentIntent)}
                  helperText={
                    errors.paymentIntent && touched.paymentIntent
                      ? errors.paymentIntent
                      : null
                  }
                />
              </Box>
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={() => submitForm()}>Save changes</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

interface NewUserPlanFormikProps {
  catalogId: string
  paymentIntent: string 
}
